import type { CompositionV3DataQuery } from '@revelio/data-access';

import { GLOBAL_MIN_START_DATE } from '../../../filters/utils/providerDateOverrides';
import {
  DataType,
  type SingleDataSet,
  type TableData,
  type TimeseriesData,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../types';
import { getEntityMonthlyValues, getEntitySeries } from '../utils/table-data';
import { trimEmptyTimeseriesEdges } from '../utils/trim-timeseries';
import {
  getLatestYoYGrowth,
  getTableMonths,
  getYoYGrowthAt,
  inferYoYPeriodOffsetFromDateRefs,
  inferYoYPeriodOffsetFromTable,
} from './shared-market-condition-utils';
import { calculateMeterValue } from './z-score/calculate-meter-value';

const POSTINGS_MIN_MONTH = GLOBAL_MIN_START_DATE.slice(0, 7);

type CompositionData = NonNullable<CompositionV3DataQuery['composition_v3']>;
type CompositionEntity = NonNullable<CompositionData['entities']>[number];

export const convertWagePressureData = ({
  data,
  isTimeseries,
  metric,
  monthRefs,
}: {
  data: CompositionEntity[];
  isTimeseries: boolean;
  metric: 'wage_pressure' | 'wage_pressure_percentile';
  monthRefs: ReadonlyMap<number, string>;
}): SingleDataSet | TimeseriesDataSet => {
  const variant =
    metric === 'wage_pressure_percentile' ? 'percentile' : 'value';

  if (isTimeseries) {
    return convertWagePressureTimeseries({
      data,
      monthRefs,
      variant,
    });
  } else {
    return convertWagePressureSingle({
      data,
      monthRefs,
      variant,
    });
  }
};

const convertWagePressureTimeseries = ({
  data,
  monthRefs,
  variant,
}: {
  data: CompositionEntity[];
  monthRefs: ReadonlyMap<number, string>;
  variant: 'value' | 'percentile';
}): TimeseriesDataSet =>
  wagePressureTimeseries({
    entities: data.map((entity) => ({
      id: String(entity.id),
      dim: entity.short_name ?? entity.long_name,
      salaries: buildSalaryValues({ entity, monthsLength: monthRefs.size }),
    })),
    months: Array.from(monthRefs.values()),
    variant,
  });

const wagePressureTimeseries = ({
  entities,
  months,
  variant,
}: {
  entities: readonly { id: string; dim: string; salaries: number[] }[];
  months: string[];
  variant: 'value' | 'percentile';
}): TimeseriesDataSet => {
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);
  const resultData: TimeseriesData[] = [];

  for (const { id, dim, salaries } of entities) {
    const timeseries: TimeseriesDataPoint[] = [];

    for (let i = periodOffset; i < months.length; i++) {
      const dateStr = months[i] ?? '';
      if (!dateStr) {
        continue;
      }
      if (dateStr < POSTINGS_MIN_MONTH) {
        continue;
      }
      const growthValue = getYoYGrowthAt({
        values: salaries,
        index: i,
        periodOffset,
      });
      timeseries.push({
        date: dateStr,
        value:
          growthValue === null
            ? null
            : toWagePressureValue({ growthValue, variant }),
      });
    }

    resultData.push({ dim, timeseries, id });
  }
  return trimEmptyTimeseriesEdges(resultData);
};

export const getWagePressureValue = ({
  entity,
  monthRefs,
  variant,
}: {
  entity: CompositionEntity;
  monthRefs: ReadonlyMap<number, string>;
  variant: 'value' | 'percentile';
}): number | null => {
  const months = Array.from(monthRefs.values());
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);
  const values = buildSalaryValues({ entity, monthsLength: monthRefs.size });
  const growthValue = getLatestYoYGrowth({ values, periodOffset });

  if (growthValue !== null) {
    return toWagePressureValue({ growthValue, variant });
  }

  const salaryData = entity.metrics.salary;
  if (salaryData.length < 2) return null;

  const firstPoint = salaryData[0];
  const lastPoint = salaryData[salaryData.length - 1];
  if (!firstPoint || !lastPoint) return null;

  const firstValue = firstPoint.val;
  const lastValue = lastPoint.val;

  if (firstValue === 0) return null;
  return toWagePressureValue({
    growthValue: (lastValue - firstValue) / firstValue,
    variant,
  });
};

export const latestWagePressure = ({
  salaries,
  periodOffset,
  variant,
}: {
  salaries: number[];
  periodOffset: number;
  variant: 'value' | 'percentile';
}): number | null => {
  const growthValue = getLatestYoYGrowth({ values: salaries, periodOffset });
  if (growthValue !== null)
    return toWagePressureValue({ growthValue, variant });

  const first = salaries[0];
  const last = salaries[salaries.length - 1];
  if (salaries.length < 2 || !first || last === undefined) return null;
  return toWagePressureValue({
    growthValue: (last - first) / first,
    variant,
  });
};

export const convertWagePressureTable = ({
  table,
  metric,
  isTimeseries,
}: {
  table: TableData;
  metric: 'wage_pressure' | 'wage_pressure_percentile';
  isTimeseries: boolean;
}): SingleDataSet | TimeseriesDataSet => {
  const periodOffset = inferYoYPeriodOffsetFromTable(table);
  const variant =
    metric === 'wage_pressure_percentile' ? 'percentile' : 'value';
  if (isTimeseries) {
    const months = getTableMonths(table);
    return wagePressureTimeseries({
      entities: getEntityMonthlyValues(table, 'salary').map(
        ({ value, byMonth }) => ({
          id: value.id,
          dim: value.label,
          salaries: months.map((month) => byMonth.get(month) ?? 0),
        })
      ),
      months,
      variant,
    });
  }
  return {
    type: DataType.SINGLE,
    data: getEntitySeries(table, 'salary').flatMap(({ value, values }) => {
      const wagePressure = latestWagePressure({
        salaries: values,
        periodOffset,
        variant,
      });
      return wagePressure === null
        ? []
        : [{ dim: value.label, value: wagePressure, id: value.id }];
    }),
  };
};

const convertWagePressureSingle = ({
  data,
  monthRefs,
  variant,
}: {
  data: CompositionEntity[];
  monthRefs: ReadonlyMap<number, string>;
  variant: 'value' | 'percentile';
}): SingleDataSet => {
  const resultData: SingleDataSet['data'] = [];

  for (const entity of data) {
    const value = getWagePressureValue({ entity, monthRefs, variant });

    if (value !== null) {
      resultData.push({
        dim: entity.short_name ?? entity.long_name,
        value,
        id: String(entity.id),
      });
    }
  }

  return { type: DataType.SINGLE, data: resultData };
};

const buildSalaryValues = ({
  entity,
  monthsLength,
}: {
  entity: CompositionEntity;
  monthsLength: number;
}): number[] => {
  const values: number[] = [];
  const salaryData = entity.metrics.salary;

  for (const point of salaryData) {
    const id = point.id;
    if (id >= 0 && id < monthsLength) {
      values.push(point.val);
    }
  }
  return values;
};

const toWagePressureValue = ({
  growthValue,
  variant,
}: {
  growthValue: number;
  variant: 'value' | 'percentile';
}): number =>
  variant === 'percentile'
    ? (calculateMeterValue({ value: growthValue, metric: 'wageGrowth' }) ?? 0)
    : growthValue;
